import React, { useState, useEffect } from 'react';
import { organizationService } from '../services/organization.service';
import { useAuth } from '../context/AuthContext';

interface OrganizationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'members' | 'activity';

export const OrganizationSettingsModal: React.FC<OrganizationSettingsModalProps> = ({ isOpen, onClose }) => {
  const { token, user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('members');
  const [orgData, setOrgData] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Invite Form State
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [inviteStatus, setInviteStatus] = useState<{ type: 'success' | 'error', msg: string } | null>(null);

  /**
   * Fetches data based on the active tab
   */
  const fetchData = async () => {
    if (!user?.organizationId || !token) return;
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'members') {
        const data = await organizationService.getOrganization(token, user.organizationId);
        setOrgData(data.organization);
      } else {
        const logData = await organizationService.getAuditLogs(token, user.organizationId);
        setLogs(logData.logs);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load team data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setInviteStatus(null);
    }
  }, [isOpen, activeTab, user?.organizationId]);

  // --- Member Management Actions ---

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.organizationId || !token) return;
    setInviteStatus(null);
    try {
      await organizationService.inviteUser(token, user.organizationId, inviteEmail, inviteRole);
      setInviteStatus({ type: 'success', msg: 'User invited successfully!' });
      setInviteEmail('');
      fetchData(); 
    } catch (err: any) {
      setInviteStatus({ type: 'error', msg: err.message });
    }
  };

  const handleRemoveUser = async (targetUserId: string) => {
    if (!user?.organizationId || !token) return;
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) return;
    try {
      await organizationService.removeUser(token, user.organizationId, targetUserId);
      fetchData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpdateRole = async (targetUserId: string, newRole: string) => {
    if (!user?.organizationId || !token) return;
    try {
      await organizationService.updateRole(token, user.organizationId, targetUserId, newRole);
      fetchData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleLeaveOrganization = async () => {
    if (!user?.organizationId || !token) return;
    if (!window.confirm('Are you sure you want to leave this organization? You will lose access to team workspaces and be downgraded to the Free tier.')) return;
    try {
      await organizationService.leaveOrganization(token, user.organizationId);
      updateUser({ tier: 'FREE', organizationId: null, orgRole: null });
      alert("You have left the organization.");
      onClose();
    } catch (err: any) {
      alert(err.message);
    }
  };

  // --- UI Helpers ---

  const formatAction = (action: string, details: any) => {
    switch(action) {
        case 'USER_INVITE': return `invited ${details?.invitedEmail || 'a new member'}`;
        case 'USER_REMOVE': return `removed a user ${details?.note ? `(${details.note})` : ''}`;
        case 'ROLE_CHANGE': return `promoted a member to ${details?.newRole}`;
        case 'DOCUMENT_CREATE': return `created a team spreadsheet`;
        case 'DOCUMENT_DELETE': return `deleted a team spreadsheet`;
        default: return action.replace('_', ' ').toLowerCase();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        
        {/* HEADER */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Team Settings</h2>
            {orgData && <p className="text-sm text-gray-500">{orgData.name}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>

        {/* TAB NAVIGATION */}
        <div className="flex border-b border-gray-100 px-6 bg-white">
            <button 
                onClick={() => setActiveTab('members')}
                className={`py-3 px-6 text-sm font-bold transition-all border-b-2 ${
                  activeTab === 'members' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
                Members
            </button>
            <button 
                onClick={() => setActiveTab('activity')}
                className={`py-3 px-6 text-sm font-bold transition-all border-b-2 ${
                  activeTab === 'activity' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
            >
                Activity Log
            </button>
        </div>

        {/* SCROLLABLE BODY */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {loading ? (
            <div className="text-center py-20 text-gray-500 font-medium animate-pulse italic">
                Syncing with server...
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg shadow-sm">
                {error}
            </div>
          ) : activeTab === 'members' && orgData ? (
            <div className="space-y-8">
              
              {/* INVITE FORM */}
              {(user?.orgRole === 'ADMIN' || user?.orgRole === 'OWNER') && (
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">Invite Team Member</h3>
                  <form onSubmit={handleInvite} className="flex flex-col md:flex-row gap-3">
                    <input 
                      type="email" 
                      placeholder="Colleague's Email Address" 
                      required
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm transition-all"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                    <select 
                      className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                    >
                      <option value="MEMBER">Member</option>
                      <option value="ADMIN">Admin</option>
                    </select>
                    <button type="submit" className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-bold transition text-sm">
                      Invite
                    </button>
                  </form>
                  {inviteStatus && (
                    <div className={`mt-3 p-2 text-xs rounded border ${
                      inviteStatus.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'
                    }`}>
                      {inviteStatus.msg}
                    </div>
                  )}
                </div>
              )}

              {/* MEMBERS LIST */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-white">
                  <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wider">Current Team ({orgData.users.length})</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {orgData.users.map((member: any) => (
                    <div key={member.id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                      <div>
                        <p className="font-bold text-sm text-gray-900">
                          {member.name} 
                          {member.id === user?.id && <span className="text-blue-500 font-medium ml-2">(You)</span>}
                        </p>
                        <p className="text-xs text-gray-500">{member.email}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        {user?.orgRole === 'OWNER' && member.id !== user.id ? (
                          <select 
                            className="p-1 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:border-red-500 font-medium"
                            value={member.orgRole}
                            onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                          >
                            <option value="MEMBER">Member</option>
                            <option value="ADMIN">Admin</option>
                            <option value="OWNER">Owner</option>
                          </select>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-[10px] font-black rounded-full uppercase tracking-widest">
                            {member.orgRole}
                          </span>
                        )}
                        
                        {(user?.orgRole === 'OWNER' || user?.orgRole === 'ADMIN') && member.id !== user.id && member.orgRole !== 'OWNER' && (
                          <button 
                            onClick={() => handleRemoveUser(member.id)} 
                            className="text-red-400 hover:text-red-600 text-xs font-bold uppercase transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : activeTab === 'activity' ? (
              <div className="space-y-3">
                  {logs.map((log: any) => (
                      <div key={log.id} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start gap-4">
                          <div className="bg-blue-50 text-blue-600 p-2 rounded-full mt-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          </div>
                          <div className="flex-1">
                              <p className="text-sm text-gray-700">
                                  <span className="font-bold text-gray-900">{log.user.name}</span> {formatAction(log.action, log.details)}
                              </p>
                              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                  {new Date(log.createdAt).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })}
                              </p>
                          </div>
                      </div>
                  ))}
                  {logs.length === 0 && (
                      <div className="text-center py-20 text-gray-400 italic">No activity recorded yet.</div>
                  )}
              </div>
          ) : null}
        </div>

        {/* FOOTER */}
        <div className="p-4 border-t border-gray-100 flex justify-between items-center bg-white px-6">
            {user?.orgRole !== 'OWNER' ? (
                 <button onClick={handleLeaveOrganization} className="text-red-500 hover:text-red-700 text-sm font-bold transition-colors">
                     Leave Organization
                 </button>
            ) : <div />}
            
            <button 
              onClick={onClose} 
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-bold transition-colors text-sm"
            >
              Close
            </button>
        </div>
      </div>
    </div>
  );
};